import { useMemo } from 'react';
import {
  TEMPLATE_VARIABLE_CATALOG,
  collectTemplateVariableKeys,
  templateVariableToken,
  type EmailTemplateContentFields,
} from '@marketing-hub/shared';
import { useToast } from '@/components/toast/ToastProvider';
import styles from './EmailTemplateEditor.module.css';

type Props = {
  mode: 'blocks' | 'html' | 'text';
  content: EmailTemplateContentFields;
  onInsert?: (token: string) => void;
};

export function EmailTemplateVariablesPanel({ mode, content, onInsert }: Props) {
  const toast = useToast();
  const detected = useMemo(() => collectTemplateVariableKeys(content), [content]);
  const exampleMode = mode === 'html' ? 'html_example' : mode === 'blocks' ? 'block_example' : 'example';

  function copyToken(key: string) {
    const token = templateVariableToken(key);
    if (onInsert) {
      onInsert(token);
      return;
    }
    void navigator.clipboard.writeText(token).then(() => {
      toast.push(`${token} gekopieerd`, 'success');
    });
  }

  return (
    <section className={styles.varsPanel} aria-label="Template-variabelen">
      <div className={styles.varsPanelHead}>
        <h3 className={styles.varsPanelTitle}>Variabelen</h3>
        <p className={styles.varsPanelHint}>
          Gebruik dubbele accolades:{' '}
          <code className={styles.varsCode}>{'{{voornaam}}'}</code> — in onderwerp, plain text, HTML
          én blokken. &quot;Invoegen&quot; gaat naar het focusveld (blok) of anders het onderwerp.
        </p>
      </div>
      {detected.length ? (
        <p className={styles.varsDetected}>
          In dit template:{' '}
          {detected.map((k) => (
            <code key={k} className={styles.varsChip}>
              {templateVariableToken(k)}
            </code>
          ))}
        </p>
      ) : null}
      <div className={styles.varsTableWrap}>
        <table className={styles.varsTable}>
          <thead>
            <tr>
              <th>Variabele</th>
              <th>Gebruik ({mode})</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {TEMPLATE_VARIABLE_CATALOG.map((row) => (
              <tr key={row.key}>
                <td>
                  <code className={styles.varsCode}>{templateVariableToken(row.key)}</code>
                  <div className={styles.varsLabel}>
                    {row.label}
                    {row.required ? ' · verplicht bij send' : ''}
                  </div>
                </td>
                <td>
                  <code className={styles.varsExample}>{row[exampleMode]}</code>
                </td>
                <td>
                  <button type="button" className={styles.varsInsertBtn} onClick={() => copyToken(row.key)}>
                    {onInsert ? 'Invoegen' : 'Kopieer'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
